import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  CirclePause,
  Clock3,
  Filter,
  Layers3,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldAlert,
  Radio,
  X,
} from 'lucide-react'
import Content from '@/layout/content/Content'
import Head from '@/layout/head/Head'
import { useTradingOverview } from '@/hooks/useTradingOverview'
import {
  buildAccountRows,
  currencyTotals,
  heartbeatSummary,
  finiteValue,
} from './tradingOverviewModel'

const money = (value) =>
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
        value,
      )
const tone = (value) =>
  value === null || value === undefined || value === 0 ? '' : value > 0 ? 'positive' : 'negative'
const time = (value) =>
  value
    ? new Date(value).toLocaleTimeString('en-GB', {
        timeZone: 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : 'Not received'

function Stat({ label, value, detail, icon: Icon, color = '' }) {
  return (
    <div className={`terminal-stat ${color}`}>
      <div className='terminal-stat-top'>
        <span>{label}</span>
        {React.createElement(Icon, { size: 17, 'aria-hidden': true })}
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  )
}

export function TradingOverviewView({
  resources,
  refreshing,
  refresh,
  intervalSeconds,
  setIntervalSeconds,
  now,
}) {
  const [query, setQuery] = useState('')
  const [broker, setBroker] = useState('all')
  const [currency, setCurrency] = useState('all')
  const [attentionOnly, setAttentionOnly] = useState(false)
  const [sort, setSort] = useState('risk')
  const rows = useMemo(
    () => buildAccountRows(resources.pnl.data, resources.risk.data, now),
    [resources.pnl.data, resources.risk.data, now],
  )
  const filtered = useMemo(
    () =>
      rows.filter(
        (row) =>
          (broker === 'all' || row.broker === broker) &&
          (currency === 'all' || row.currency === currency) &&
          (!attentionOnly || row.attention || row.dataIssue) &&
          `${row.broker} ${row.account}`.toLowerCase().includes(query.toLowerCase().trim()),
      ),
    [rows, broker, currency, attentionOnly, query],
  )
  const ordered = useMemo(
    () =>
      [...filtered].sort((a, b) =>
        sort === 'pnl'
          ? a.currency.localeCompare(b.currency) || (a.total ?? Infinity) - (b.total ?? Infinity)
          : a.priority - b.priority ||
            (b.exposure ?? -1) - (a.exposure ?? -1) ||
            a.key.localeCompare(b.key),
      ),
    [filtered, sort],
  )
  const totals = currencyTotals(filtered)
  const heartbeat = heartbeatSummary(resources.heartbeat.data, now)
  const riskAccounts = resources.risk.data?.accounts || []
  const riskAlerts = [...riskAccounts]
    .filter(
      (item) =>
        item.should_block_new_orders === true ||
        item.should_close_account === true ||
        item.lock_for_day === true ||
        item.status === 'ACTIVE' ||
        item.risk_state === 'BAD_DAY_ARMED' ||
        item.bad_day_armed === true ||
        item.close_error,
    )
    .sort(
      (a, b) =>
        Number(b.should_block_new_orders === true || b.status === 'ACTIVE') -
        Number(a.should_block_new_orders === true || a.status === 'ACTIVE'),
    )
  const failures = Object.entries(resources).filter(([, source]) => source.error)
  const missing = Object.values(resources).filter((source) => source.data === null).length
  const riskUnavailable = Boolean(resources.risk.error) || !resources.risk.data
  const hasFilters = query || broker !== 'all' || currency !== 'all' || attentionOnly
  const clear = () => {
    setQuery('')
    setBroker('all')
    setCurrency('all')
    setAttentionOnly(false)
  }
  const statusLabel = failures.length
    ? 'Connection needs attention'
    : refreshing && missing
      ? 'Connecting to feeds'
      : missing
        ? 'Awaiting data'
        : intervalSeconds === 0
          ? 'Auto-refresh paused'
          : 'Monitoring feeds'

  return (
    <Content>
      <Head title='Trading overview' />
      <main className='trading-terminal' id='trading-main-content' tabIndex={-1}>
        <div className='terminal-heading'>
          <div>
            <div className='terminal-eyebrow'>
              <span className='terminal-mark' /> ALGOREASON / TRADING DESK
            </div>
            <h1>
              Trading overview<span>.</span>
            </h1>
            <p>Your accounts, exposure, and execution health. One clear view.</p>
          </div>
          <div className='terminal-controls'>
            <span className='terminal-clock'>
              <Clock3 size={14} />
              {new Date(now).toLocaleTimeString('en-GB', { timeZone: 'UTC' })} UTC
            </span>
            <label className='visually-hidden' htmlFor='overview-refresh'>
              Auto-refresh interval
            </label>
            <select
              id='overview-refresh'
              value={intervalSeconds}
              onChange={(event) => setIntervalSeconds(Number(event.target.value))}
            >
              <option value={30}>Every 30s</option>
              <option value={60}>Every 60s</option>
              <option value={120}>Every 2m</option>
              <option value={0}>Paused</option>
            </select>
            <button
              className='terminal-button primary'
              type='button'
              onClick={refresh}
              disabled={refreshing}
            >
              <RefreshCw size={15} className={refreshing ? 'terminal-spin' : ''} />
              {refreshing ? 'Refreshing' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className={`terminal-feed-bar ${failures.length ? 'degraded' : ''}`} role='status'>
          <span>
            <span
              className={`terminal-dot ${failures.length || missing ? 'warning' : intervalSeconds === 0 ? 'paused' : ''}`}
            />
            {statusLabel}
          </span>
          <div>
            {Object.entries(resources).map(([name, source]) => (
              <span key={name} className={source.error ? 'terminal-text-warning' : ''}>
                {{ pnl: 'Accounts', risk: 'Risk', heartbeat: 'Bots' }[name]}{' '}
                <b>{source.error ? 'unavailable' : time(source.receivedAt)}</b>
              </span>
            ))}
          </div>
        </div>
        {failures.length > 0 && (
          <div className='terminal-error' role='alert'>
            <ShieldAlert size={18} />
            <div>
              <strong>Some data could not be refreshed.</strong>
              <span>
                {failures
                  .map(
                    ([name, source]) =>
                      `${{ pnl: 'Accounts', risk: 'Risk', heartbeat: 'Bots' }[name]}: ${source.error}`,
                  )
                  .join(' · ')}
                . Previous snapshots remain visible and may be out of date.
              </span>
            </div>
            <button
              className='terminal-button'
              type='button'
              onClick={refresh}
              disabled={refreshing}
            >
              Retry
            </button>
          </div>
        )}

        <section className='terminal-stats' aria-label='Desk summary'>
          <Stat
            label='Reported accounts'
            value={resources.pnl.data === null ? '—' : rows.length}
            detail={`${new Set(rows.map((row) => row.broker)).size} brokers · current P&L snapshots`}
            icon={Layers3}
          />
          <Stat
            label='Open positions'
            value={
              resources.pnl.data === null || rows.some((row) => row.positions === null)
                ? '—'
                : rows.reduce((sum, row) => sum + row.positions, 0)
            }
            detail='Reported across all accounts'
            icon={Activity}
          />
          <Stat
            label='Risk alerts'
            value={resources.risk.data === null ? '—' : riskAlerts.length}
            detail={riskUnavailable ? 'Risk feed not current' : 'Breakers, locks & armed states'}
            icon={ShieldAlert}
            color={riskAlerts.length || riskUnavailable ? 'warning' : ''}
          />
          <Stat
            label='Heartbeat online'
            value={heartbeat ? `${heartbeat.online} / ${heartbeat.total}` : '—'}
            detail={
              resources.heartbeat.error
                ? 'Last snapshot · refresh failed'
                : heartbeat
                  ? `${heartbeat.stale} stale · ${heartbeat.offline} without heartbeat`
                  : 'Awaiting heartbeat feed'
            }
            icon={Radio}
          />
        </section>

        <div className='terminal-main-grid'>
          <div className='terminal-primary'>
            <section className='terminal-panel terminal-portfolio'>
              <div className='terminal-panel-heading'>
                <div>
                  <span className='terminal-eyebrow'>PORTFOLIO SNAPSHOT</span>
                  <h2>P&L by account currency</h2>
                </div>
                <span className='terminal-caption'>No cross-currency conversion</span>
              </div>
              <div className='terminal-currencies'>
                {totals.length ? (
                  totals.map((group) => (
                    <article className='terminal-currency' key={group.currency}>
                      <div className='terminal-currency-title'>
                        <span>{group.currency}</span>
                        <small>
                          {group.accounts} {group.accounts === 1 ? 'account' : 'accounts'}
                        </small>
                      </div>
                      <div className={`terminal-pnl-value ${tone(group.total)}`}>
                        {group.total > 0 ? '+' : ''}
                        {money(group.total)}
                        {group.incomplete && <small> *</small>}
                      </div>
                      <div className='terminal-currency-details'>
                        <span>
                          Realized <b className={tone(group.realized)}>{money(group.realized)}</b>
                        </span>
                        <span>
                          Floating <b className={tone(group.floating)}>{money(group.floating)}</b>
                        </span>
                      </div>
                      <div className='terminal-currency-note'>
                        {group.incomplete
                          ? '* Partial totals: some amounts missing.'
                          : group.stale || resources.pnl.error
                            ? 'Includes snapshots that may be out of date.'
                            : 'Reported P&L · account currency'}
                      </div>
                    </article>
                  ))
                ) : (
                  <div className='terminal-empty compact'>
                    {refreshing && !resources.pnl.data
                      ? 'Loading account snapshots…'
                      : hasFilters
                        ? 'No accounts match these filters.'
                        : 'No account snapshots available.'}
                  </div>
                )}
              </div>
            </section>

            <section className='terminal-panel'>
              <div className='terminal-panel-heading'>
                <div>
                  <span className='terminal-eyebrow'>ACCOUNT MONITOR</span>
                  <h2>Risk first. Details one click away.</h2>
                </div>
                <span className='terminal-count'>
                  {ordered.length} / {rows.length}
                </span>
              </div>
              <div className='terminal-filters'>
                <label className='terminal-search'>
                  <Search size={16} />
                  <span className='visually-hidden'>Search broker or account</span>
                  <input
                    placeholder='Search broker or account…'
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </label>
                <label>
                  <span className='visually-hidden'>Filter broker</span>
                  <select
                    aria-label='Filter broker'
                    value={broker}
                    onChange={(event) => setBroker(event.target.value)}
                  >
                    <option value='all'>All brokers</option>
                    {[...new Set(rows.map((row) => row.broker))].sort().map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className='visually-hidden'>Filter currency</span>
                  <select
                    aria-label='Filter currency'
                    value={currency}
                    onChange={(event) => setCurrency(event.target.value)}
                  >
                    <option value='all'>All currencies</option>
                    {[...new Set(rows.map((row) => row.currency))].sort().map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <button
                  className={`terminal-button ${attentionOnly ? 'selected' : ''}`}
                  type='button'
                  aria-pressed={attentionOnly}
                  onClick={() => setAttentionOnly(!attentionOnly)}
                >
                  <Filter size={14} />
                  Needs attention
                </button>
                {hasFilters && (
                  <button
                    className='terminal-icon-button'
                    type='button'
                    aria-label='Clear filters'
                    onClick={clear}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <div className='terminal-table-toolbar'>
                <span>
                  <span className='terminal-dot paused' />
                  {resources.pnl.error || resources.risk.error
                    ? 'Showing last available data'
                    : 'Read-only monitoring · no order placement'}
                </span>
                <label>
                  Sort{' '}
                  <select
                    aria-label='Sort accounts'
                    value={sort}
                    onChange={(event) => setSort(event.target.value)}
                  >
                    <option value='risk'>Risk priority</option>
                    <option value='pnl'>P&L: lowest first, by currency</option>
                  </select>
                </label>
              </div>
              <div className='terminal-table-scroll'>
                <table className='terminal-table'>
                  <caption className='visually-hidden'>
                    Trading account performance, risk status and snapshot time
                  </caption>
                  <thead>
                    <tr>
                      <th>Account / broker</th>
                      <th>Currency</th>
                      <th className='numeric'>Balance</th>
                      <th className='numeric'>Floating P&L</th>
                      <th className='numeric'>Total P&L</th>
                      <th className='numeric'>Positions</th>
                      <th>Protection</th>
                      <th>Snapshot</th>
                      <th>
                        <span className='visually-hidden'>Details</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordered.map((row) => (
                      <tr key={row.key} className={row.attention ? 'attention-row' : ''}>
                        <td>
                          <strong>{row.account}</strong>
                          <span className='terminal-broker'>{row.broker}</span>
                        </td>
                        <td>
                          <span className='terminal-currency-tag'>{row.currency}</span>
                        </td>
                        <td className='numeric'>{money(row.balance)}</td>
                        <td className={`numeric ${tone(row.floating)}`}>{money(row.floating)}</td>
                        <td className={`numeric total ${tone(row.total)}`}>
                          {row.total > 0 ? '+' : ''}
                          {money(row.total)}
                        </td>
                        <td className='numeric'>{row.positions ?? '—'}</td>
                        <td>
                          <Link
                            className={`terminal-status ${resources.risk.error ? 'warning' : row.protection.color}`}
                            to='/circuit-breakers'
                          >
                            {resources.risk.error ? 'UNVERIFIED' : row.protection.label}
                          </Link>
                          {row.exposure !== null && (
                            <div
                              className='terminal-exposure'
                              title={`${row.exposure.toFixed(0)}% of hard loss threshold`}
                            >
                              <span style={{ width: `${row.exposure}%` }} />
                            </div>
                          )}
                        </td>
                        <td
                          className={
                            row.stale || resources.pnl.error ? 'terminal-text-warning' : ''
                          }
                        >
                          <span>{row.updatedAt ? time(row.updatedAt) : 'Unknown'}</span>
                          <small>
                            {row.stale
                              ? 'Stale / check source'
                              : resources.pnl.error
                                ? 'Refresh failed'
                                : 'Source timestamp'}
                          </small>
                        </td>
                        <td>
                          <Link
                            className='terminal-icon-button'
                            to={`/daily-pnl?account=${encodeURIComponent(row.key)}`}
                            aria-label={`View ${row.broker} account ${row.account}`}
                          >
                            <ArrowUpRight size={17} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!ordered.length && (
                <div className='terminal-empty'>
                  <Layers3 size={25} />
                  <strong>
                    {refreshing && !resources.pnl.data
                      ? 'Connecting to account data…'
                      : hasFilters
                        ? 'No matching accounts'
                        : 'No account data yet'}
                  </strong>
                  <span>
                    {hasFilters
                      ? 'Try a different broker or clear your filters.'
                      : 'Account snapshots will appear when the API reports them.'}
                  </span>
                  {hasFilters && (
                    <button type='button' className='terminal-button' onClick={clear}>
                      Clear filters
                    </button>
                  )}
                </div>
              )}
              <div className='terminal-panel-footer'>
                <span>
                  Risk priority → loss exposure → account. Amounts remain in native currency.
                </span>
                <Link to='/daily-pnl'>
                  Full P&L report <ArrowRight size={13} />
                </Link>
              </div>
            </section>
          </div>

          <aside className='terminal-aside'>
            <section className='terminal-panel'>
              <div className='terminal-panel-heading'>
                <div>
                  <span className='terminal-eyebrow'>ATTENTION QUEUE</span>
                  <h2>Execution guardrails</h2>
                </div>
                <ShieldCheck size={21} />
              </div>
              {riskUnavailable && (
                <div className='terminal-inline-warning'>
                  Risk verification unavailable.{' '}
                  {resources.risk.data
                    ? 'Showing last reported alerts.'
                    : 'No protection status can be confirmed.'}
                </div>
              )}
              {riskAlerts.length ? (
                <div className='terminal-alerts'>
                  {riskAlerts.slice(0, 5).map((item) => (
                    <Link
                      className='terminal-alert-item'
                      to='/circuit-breakers'
                      key={`${item.broker}:${item.account_id}`}
                    >
                      <span className='terminal-alert-icon'>
                        <ShieldAlert size={17} />
                      </span>
                      <div>
                        <strong>
                          {item.broker} <span>#{item.account_id}</span>
                        </strong>
                        <p>
                          {item.should_close_account
                            ? 'Account close requested'
                            : item.lock_for_day
                              ? 'Locked for the day'
                              : item.should_block_new_orders
                                ? 'New orders blocked'
                                : item.status === 'ACTIVE'
                                  ? 'Circuit breaker active'
                                  : item.close_error
                                    ? 'Close operation needs review'
                                    : 'Bad-day protection armed'}
                        </p>
                        <small>
                          {finiteValue(item.total_pnl) === null
                            ? 'P&L not reported'
                            : `Reported P&L ${money(Number(item.total_pnl))} · see account currency`}
                        </small>
                      </div>
                      <ArrowUpRight size={15} />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className='terminal-empty compact'>
                  <ShieldCheck size={27} />
                  <strong>
                    {riskUnavailable ? 'Waiting for risk data' : 'No execution alerts reported'}
                  </strong>
                  <span>Reported states only. Check account data quality before acting.</span>
                </div>
              )}
              <Link className='terminal-aside-link' to='/circuit-breakers'>
                Review all circuit breakers <ArrowRight size={14} />
              </Link>
            </section>
            <section className='terminal-panel terminal-workflow'>
              <span className='terminal-eyebrow'>DESK WORKFLOW</span>
              <h2>Go deeper</h2>
              {[
                [
                  '/signals-dashboard',
                  'Signal monitor',
                  'Direction, temperature & history',
                  Activity,
                ],
                ['/bots-dashboard', 'Bot operations', 'Heartbeat and execution status', Radio],
                [
                  '/spread-summary',
                  'Execution costs',
                  'Spread quality by instrument',
                  ArrowDownRight,
                ],
                ['/trade-stats', 'Performance review', 'Trade outcomes and statistics', Layers3],
              ].map(([to, title, description, Icon]) => (
                <Link to={to} key={to}>
                  <span className='terminal-workflow-icon'>
                    {React.createElement(Icon, { size: 18 })}
                  </span>
                  <div>
                    <strong>{title}</strong>
                    <small>{description}</small>
                  </div>
                  <ArrowRight size={15} />
                </Link>
              ))}
            </section>
            <div className='terminal-desk-note'>
              <CirclePause size={15} />
              <p>
                {intervalSeconds === 0
                  ? 'Automatic refresh is paused. Use Refresh for a new snapshot.'
                  : `Snapshots refresh every ${intervalSeconds}s while this tab is visible. This is monitoring data, not a live executable quote.`}
              </p>
            </div>
          </aside>
        </div>
      </main>
    </Content>
  )
}

export default function TradingOverview() {
  const [intervalSeconds, setIntervalSeconds] = useState(60)
  const [now, setNow] = useState(Date.now())
  const feed = useTradingOverview(intervalSeconds)
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  return (
    <TradingOverviewView
      {...feed}
      now={now}
      intervalSeconds={intervalSeconds}
      setIntervalSeconds={setIntervalSeconds}
    />
  )
}
